import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import Remove from "@mui/icons-material/Remove";
import Close from "@mui/icons-material/Close";
import { Controller, useFormContext } from "react-hook-form";
import { AgencyPickerLayout } from "./AgencyPickerLayout";
import { useSupplementalAgencies } from "./useSupplementalAgencies";
import { DistanceDisplay } from "./DistanceDisplay";
import { isPOBoxAddress } from "encamp-shared/src/utils/address";

interface AgencyFragment {
  id: string;
  name?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  state?: string | null;
  county?: string | null;
  mailingAddress?: string | null;
}

interface SupplementalAgencySectionProps {
  disabled?: boolean;
  readOnly?: boolean;
  fieldName: any; // Allow any field name for flexibility
  agencyKey: string; // "fireDepartment" or "lepc"
  existingAgencies?: Array<any> | null;
  addButtonText: string;
  helperText: string;
  PickerComponent: React.ComponentType<any>;
  facilityLatitude?: number | null;
  facilityLongitude?: number | null;
  pickerDescription?: string;
}

export function SupplementalAgencySection({
  disabled = false,
  readOnly = false,
  fieldName,
  agencyKey,
  existingAgencies = null,
  addButtonText,
  helperText,
  PickerComponent,
  facilityLatitude,
  facilityLongitude,
  pickerDescription,
}: SupplementalAgencySectionProps) {
  const { control } = useFormContext();
  const theme = useTheme();
  const [confirmRemoveIndex, setConfirmRemoveIndex] = useState<number | null>(
    null
  );

  const {
    supplementalFields,
    supplementalSelections,
    handleSelectionChange,
    handleAdd,
    handleRemove,
  } = useSupplementalAgencies({
    control,
    fieldName,
    existingAgencies,
    agencyKey,
  });

  const handleConfirmRemove = () => {
    if (confirmRemoveIndex !== null) {
      handleRemove(confirmRemoveIndex);
      setConfirmRemoveIndex(null);
    }
  };

  const handleCancelRemove = () => {
    setConfirmRemoveIndex(null);
  };

  if (supplementalFields.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Button
          onClick={handleAdd}
          disabled={
            readOnly ||
            disabled ||
            (supplementalFields.length > 0 &&
              !supplementalSelections[supplementalSelections.length - 1])
          }
          startIcon={<Add />}
          sx={{
            textTransform: "uppercase",
            fontSize: "0.75rem",
            fontWeight: 600,
            color: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
            "&.Mui-disabled": {
              color: theme.palette.grey[400],
            },
          }}
        >
          {addButtonText}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {supplementalFields.map((field, index) => (
        <Box key={field.id} sx={{ mb: 3 }}>
          <AgencyPickerLayout
            selectedAgency={supplementalSelections[index]}
            facilityLatitude={facilityLatitude}
            facilityLongitude={facilityLongitude}
            pickerDescription={pickerDescription}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Controller
                  name={`${fieldName}.${index}` as const}
                  control={control}
                  render={({ field: formField, fieldState }) => (
                    <PickerComponent
                      {...formField}
                      {...fieldState}
                      helperText={helperText}
                      facilityIds={[]}
                      disabled={readOnly}
                      onChange={(agency: AgencyFragment | null) => {
                        handleSelectionChange(index, agency);
                        formField.onChange(agency?.id ?? null);
                      }}
                      value={supplementalSelections[index]}
                    />
                  )}
                />
              </Box>
              <Button
                onClick={() => {
                  if (supplementalSelections[index]) {
                    setConfirmRemoveIndex(index);
                  } else {
                    handleRemove(index);
                  }
                }}
                disabled={readOnly}
                sx={{
                  minWidth: "auto",
                  p: 1,
                  mt: 1,
                  border: "none",
                  color: theme.palette.grey[600],
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: theme.palette.grey[100],
                    border: "none",
                  },
                }}
              >
                <Remove />
              </Button>
            </Box>
          </AgencyPickerLayout>
        </Box>
      ))}

      <Box sx={{ mt: 2 }}>
        <Button
          onClick={handleAdd}
          disabled={
            readOnly ||
            disabled ||
            (supplementalFields.length > 0 &&
              !supplementalSelections[supplementalSelections.length - 1])
          }
          startIcon={<Add />}
          sx={{
            textTransform: "uppercase",
            fontSize: "0.75rem",
            fontWeight: 600,
            color: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
            "&.Mui-disabled": {
              color: theme.palette.grey[400],
            },
          }}
        >
          {addButtonText}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmRemoveIndex !== null}
        onClose={handleCancelRemove}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          Are you sure you want to remove this agency?
          <IconButton
            aria-label="close"
            onClick={handleCancelRemove}
            sx={{
              color: theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {confirmRemoveIndex !== null &&
            supplementalSelections[confirmRemoveIndex] &&
            (() => {
              const agency = supplementalSelections[confirmRemoveIndex];
              const isPoBox = isPOBoxAddress(agency?.mailingAddress);

              return (
                <Grid container sx={{ mx: 1 }} spacing={2}>
                  {/* Agency Name Row */}
                  <Grid size={3}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.grey[600],
                        fontWeight: 500,
                      }}
                    >
                      {fieldName === "supplementalLepcIds"
                        ? "LEPC"
                        : "Fire District"}
                    </Typography>
                  </Grid>
                  <Grid size={9}>
                    <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                      {agency?.name || "Unknown Agency"}
                      {agency?.city &&
                        agency?.state &&
                        `, ${agency.city}, ${agency.state}`}
                    </Typography>
                  </Grid>

                  {/* Distance Row - only show if not a PO Box and has coordinates */}
                  {!isPoBox &&
                    facilityLatitude &&
                    facilityLongitude &&
                    agency?.latitude &&
                    agency?.longitude && (
                      <>
                        <Grid size={3}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.grey[600],
                              fontWeight: 500,
                            }}
                          >
                            Distance
                          </Typography>
                        </Grid>
                        <Grid size={9}>
                          <DistanceDisplay
                            facilityCoordinates={{
                              latitude: facilityLatitude,
                              longitude: facilityLongitude,
                            }}
                            agencyCoordinates={{
                              latitude: agency.latitude,
                              longitude: agency.longitude,
                            }}
                            sx={{
                              marginTop: 0,
                              fontSize: "1rem",
                              textAlign: "left",
                            }}
                          />
                        </Grid>
                      </>
                    )}
                </Grid>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmRemove}
            sx={{ color: theme.palette.primary.main }}
          >
            YES, REMOVE
          </Button>
          <Button
            onClick={handleCancelRemove}
            sx={{ color: theme.palette.primary.main }}
          >
            CANCEL
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
